import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface BreadcrumbOverrides {
  [path: string]: string;
}

interface BreadcrumbsContextType {
  overrides: BreadcrumbOverrides;
  setOverride: (path: string, label: string) => void;
  clearOverride: (path: string) => void;
  clearAllOverrides: () => void;
  linkOverrides: BreadcrumbOverrides;
  setLinkOverride: (path: string, linkTo: string) => void;
  clearLinkOverride: (path: string) => void;
  // Last query string seen for each pathname, so a breadcrumb link back to a
  // list page (e.g. "Clients", "Jobs") restores the filters/page the user
  // had there, instead of always landing on a bare, param-less URL.
  visitedSearchByPath: BreadcrumbOverrides;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextType | undefined>(undefined);

export const BreadcrumbsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [overrides, setOverrides] = useState<BreadcrumbOverrides>({});
  const [linkOverrides, setLinkOverrides] = useState<BreadcrumbOverrides>({});
  const [visitedSearchByPath, setVisitedSearchByPath] = useState<BreadcrumbOverrides>({});
  const location = useLocation();

  useEffect(() => {
    setVisitedSearchByPath((prev) =>
      prev[location.pathname] === location.search
        ? prev
        : { ...prev, [location.pathname]: location.search }
    );
  }, [location.pathname, location.search]);

  const setOverride = useCallback((path: string, label: string) => {
    setOverrides((prev) => ({ ...prev, [path]: label }));
  }, []);

  const clearOverride = useCallback((path: string) => {
    setOverrides((prev) => {
      const newOverrides = { ...prev };
      delete newOverrides[path];
      return newOverrides;
    });
  }, []);

  const clearAllOverrides = useCallback(() => {
    setOverrides({});
  }, []);

  const setLinkOverride = useCallback((path: string, linkTo: string) => {
    setLinkOverrides((prev) => ({ ...prev, [path]: linkTo }));
  }, []);

  const clearLinkOverride = useCallback((path: string) => {
    setLinkOverrides((prev) => {
      const newOverrides = { ...prev };
      delete newOverrides[path];
      return newOverrides;
    });
  }, []);

  return (
    <BreadcrumbsContext.Provider value={{ overrides, setOverride, clearOverride, clearAllOverrides, linkOverrides, setLinkOverride, clearLinkOverride, visitedSearchByPath }}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbsContext);
  if (context === undefined) {
    throw new Error("useBreadcrumbs must be used within a BreadcrumbsProvider");
  }
  return context;
};
